try {
    if (process.env.TOKENS_FEATURE_FLAG === 'true' || process.env.STORYBOOK_TOKENS_FEATURE_FLAG !== 'false') require('./index-new.css');
    else require('./index.css');
  } catch (e) {
    if (e.code !== 'MODULE_NOT_FOUND') {
      throw e; // Re-throw unexpected errors
    }
    console.warn('index.css / index-new.css not found, proceeding without it.');
  }


import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import classNames from 'classnames';
import { forwardRef, useState, useEffect, useCallback } from 'react';
import { useQueryClient, useQuery, QueryClient, QueryClientProvider } from 'react-query';
import { Button } from '@design-system/button';
import { CheckboxField } from '@design-system/checkbox-field';
import { CheckboxList } from '@design-system/checkbox-list';
import { DateField } from '@design-system/date-field';
import { ErrorMessage } from '@design-system/error-message';
import { InputField } from '@design-system/input-field';
import { LegalField } from '@design-system/legal-field';
import { LongTextField } from '@design-system/long-text-field';
import { GoogleReCaptchaProvider, GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { getQueries, useConditionsWatch } from '@promo-platform/utils';
import { isBefore, isAfter, differenceInYears } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { FieldHead } from '@design-system/field-head';
import { Icon } from '@design-system/icon';
import { HeadingBlock } from '@design-system/heading-block';
import { Loader } from '@design-system/loader';
import { RadioList } from '@design-system/radio-list';
import { Dropdown } from '@design-system/dropdown';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

const getFormData = (url, id) => __awaiter(void 0, void 0, void 0, function* () {
    const { data } = yield axios.get(`${url}/${id}`);
    return data;
});

const sendFormData = ({ url, formId, body, token }) => __awaiter(void 0, void 0, void 0, function* () {
    const { data } = yield axios.post(`${url}/${formId}/response`, {
        responses: body,
        captchaToken: token || null,
    });
    return data;
});

var FORM;
(function (FORM) {
    FORM["FORM_ID"] = "form-creator-form";
    FORM["ID"] = "id";
    FORM["NAME_DESCRIPTION_CARD_ID"] = "form-name-description-card";
    FORM["RESULT_CARD_ID"] = "form-result-card";
    FORM["FIELD_ARRAY_ID"] = "fieldArrayId";
    FORM["TITLE"] = "title";
    FORM["DESCRIPTION"] = "description";
    FORM["START_DATE_TIME_SETTINGS"] = "startDateSettings";
    FORM["END_DATE_TIME_SETTINGS"] = "endDateSettings";
    FORM["DATE_TIME"] = "localDateTime";
    FORM["REDIRECT_URL"] = "redirectUrl";
    FORM["TIME_ZONE"] = "timeZoneInfo";
    FORM["SUBMIT_BUTTON_TEXT"] = "submitButtonText";
    FORM["SUBMIT_BUTTON_ARIA_LABEL"] = "submitButtonDescription";
    FORM["BACK_BUTTON_TEXT"] = "backButtonText";
    FORM["BACK_BUTTON_ARIA_LABEL"] = "backButtonDescription";
    FORM["THANK_YOU_PAGE_URL"] = "thankYouPageUrl";
    FORM["LABEL"] = "label";
    FORM["ALIAS"] = "alias";
    FORM["TYPE"] = "type";
    FORM["TOOLTIP_TEXT"] = "tooltip";
    FORM["TOOLTIP_LABEL"] = "tooltipLabel";
    FORM["SECTIONS"] = "sections";
    FORM["FIELDS"] = "fields";
    FORM["PLACEHOLDER"] = "placeholder";
    FORM["ERROR_MESSAGE"] = "errorMessage";
    FORM["VALUE"] = "value";
    FORM["OPERATOR"] = "operator";
    FORM["IS_SELECTED"] = "isSelected";
    FORM["MANDATORY_VALIDATION"] = "mandatoryValidation";
    FORM["MIN_LENGTH_VALIDATION"] = "minLengthValidation";
    FORM["MAX_LENGTH_VALIDATION"] = "maxLengthValidation";
    FORM["MAX_FILE_SIZE_VALIDATION"] = "maxFileSizeValidation";
    FORM["MAX_NUMBER_OF_ANSWERS_VALIDATION"] = "maxNumberOfAnswersValidation";
    FORM["MIN_NUMBER_OF_ANSWERS_VALIDATION"] = "minNumberOfAnswersValidation";
    FORM["MAX_VALUE_VALIDATION"] = "maxValueValidation";
    FORM["MIN_VALUE_VALIDATION"] = "minValueValidation";
    FORM["PATTERN_VALIDATION"] = "patternValidation";
    FORM["ACCEPTED_FILE_TYPES_VALIDATION"] = "acceptedFileTypesValidation";
    FORM["DATE_LIMITATION_VALIDATION"] = "dateLimitationValidation";
    FORM["ACCEPTED_FILE_TYPES"] = "acceptedFileTypes";
    FORM["LIST_OF_VALUES"] = "listOfValues";
    FORM["NUMBER_OF_ROWS"] = "numberOfRows";
    FORM["LEGAL_DOCUMENT_SOURCE_TYPE"] = "legalDocumentSourceType";
    FORM["DEFAULT_VALUE"] = "defaultValue";
    FORM["RECAPTCHA_SITE_KEY"] = "recaptchaSiteKey";
    FORM["SEND_DATA_ON_EACH_SECTION_SUBMIT"] = "sendDataOnEachSectionSubmit";
    FORM["IS_HIDDEN"] = "isHidden";
    FORM["CONDITIONS"] = "conditions";
    FORM["SEND_TO_THANK_YOU_PAGE"] = "sendToThankYouPage";
})(FORM || (FORM = {}));

var FORM_TYPES;
(function (FORM_TYPES) {
    FORM_TYPES["ShortTextField"] = "ShortTextField";
    FORM_TYPES["LongTextField"] = "LongTextField";
    FORM_TYPES["BooleanField"] = "BooleanField";
    FORM_TYPES["SelectListField"] = "SelectListField";
    FORM_TYPES["SelectField"] = "SelectField";
    FORM_TYPES["AttachFileField"] = "AttachFileField";
    FORM_TYPES["LegalField"] = "LegalField";
    FORM_TYPES["DateField"] = "DateField";
    FORM_TYPES["IntegerField"] = "IntegerField";
    FORM_TYPES["CheckboxList"] = "CheckboxList";
})(FORM_TYPES || (FORM_TYPES = {}));
var OperatorType;
(function (OperatorType) {
    OperatorType["GT"] = "Gt";
    OperatorType["LT"] = "Lt";
})(OperatorType || (OperatorType = {}));

const parseRegex = (regex) => {
    if (!regex) {
        return undefined;
    }
    const parts = /\/(.*)\/(.*)/.exec(`${regex}`) || /\/(.*)\/(.*)/.exec(`/${regex}/`);
    return parts ? new RegExp(parts[1], parts[2]) : undefined;
};
const prepareLegalBody = ({ formData, field, queryClient, }) => {
    const queries = getQueries(field.description);
    const ldsArray = queries
        .map((lds) => {
        const data = queryClient.getQueryData(lds.data.legalDocumentId);
        if (!data) {
            return null;
        }
        return {
            documentId: data.documentId || lds.data.legalDocumentId,
            revisionId: data.revisionId || data.versionId,
        };
    })
        .filter((item) => item);
    return {
        type: field.type,
        id: field.id,
        value: formData[field.alias],
        legalDocuments: ldsArray.length > 0 ? ldsArray : null,
    };
};
const prepareBody = (requestData, formData, queryClient) => {
    const body = requestData.sections.map(({ fields }) => fields
        .filter((field) => { var _a; return !((_a = field.conditions) === null || _a === void 0 ? void 0 : _a.length) || field.alias in formData; })
        .map((field) => {
        var _a, _b;
        if (field.type === FORM_TYPES.AttachFileField) {
            const fileValue = ((_a = formData[field.alias]) === null || _a === void 0 ? void 0 : _a.length) > 0
                ? formData[field.alias].map((file) => file.value)
                : null;
            return {
                type: field.type,
                id: field.id,
                value: fileValue,
            };
        }
        if (field.type === FORM_TYPES.LegalField) {
            return prepareLegalBody({ formData, field, queryClient });
        }
        if (field.type === FORM_TYPES.SelectListField) {
            return {
                type: field.type,
                id: field.id,
                value: typeof formData[field.alias] === 'string'
                    ? [formData[field.alias]]
                    : formData[field.alias],
            };
        }
        if (field.type === FORM_TYPES.BooleanField) {
            return {
                type: field.type,
                id: field.id,
                value: formData[field.alias] || false,
            };
        }
        return {
            type: field.type,
            id: field.id,
            value: ((_b = formData[field.alias]) === null || _b === void 0 ? void 0 : _b.value) || formData[field.alias] || null,
        };
    }));
    return body.flat();
};
const findUtmParemeter = (label, utmParameters) => utmParameters === null || utmParameters === void 0 ? void 0 : utmParameters.find(({ name }) => name === label);
const mapApiResponse = ({ apiProps, settings, utmParameters, }) => {
    var _a;
    switch (apiProps.type) {
        case FORM_TYPES.SelectListField:
        case FORM_TYPES.SelectField:
            return Object.assign(Object.assign({}, apiProps), { options: apiProps.listOfValues.map(({ alias, value, isSelected }) => ({
                    value: alias,
                    label: value,
                    isSelected,
                })) });
        case FORM_TYPES.LegalField:
            return Object.assign(Object.assign({}, apiProps), { type: 'checkbox', config: settings === null || settings === void 0 ? void 0 : settings.config });
        case FORM_TYPES.BooleanField:
            return Object.assign(Object.assign({}, apiProps), { type: 'checkbox' });
        case FORM_TYPES.ShortTextField:
            return Object.assign(Object.assign({}, apiProps), { defaultValue: (apiProps.isHidden &&
                    ((_a = findUtmParemeter(apiProps.label, utmParameters)) === null || _a === void 0 ? void 0 : _a.value)) ||
                    apiProps.defaultValue });
        default:
            return apiProps;
    }
};
const transpileValidationRule = (rule) => {
    if (!(rule === null || rule === void 0 ? void 0 : rule.value))
        return undefined;
    const { value, errorMessage = '' } = rule;
    return { value, message: errorMessage };
};
const validateDateLimitation = (value, dateLimitationValidation) => {
    if (!dateLimitationValidation)
        return undefined;
    const days = differenceInYears(new Date(), new Date(value));
    const zeroNumber = 0;
    if (days < zeroNumber) {
        return dateLimitationValidation.errorMessage;
    }
    if (dateLimitationValidation.operator === OperatorType.GT &&
        days < dateLimitationValidation.value) {
        return dateLimitationValidation.errorMessage;
    }
    if (dateLimitationValidation.operator === OperatorType.LT &&
        days > dateLimitationValidation.value) {
        return dateLimitationValidation.errorMessage;
    }
    return undefined;
};
const prepareFieldProps = ({ apiProps, settings, utmParameters, register, errors, setValue, uniqueFormPrefix, }) => {
    var _a;
    const fieldProps = mapApiResponse({
        apiProps,
        settings,
        utmParameters,
    });
    if ('sendToThankYouPage' in fieldProps)
        delete fieldProps.sendToThankYouPage;
    const { alias, defaultValue, mandatoryValidation, minLengthValidation, maxLengthValidation, patternValidation, dateLimitationValidation, tooltip, sendToThankYouPage: _sendToThankYouPage } = fieldProps, restFieldProps = __rest(fieldProps, ["alias", "defaultValue", "mandatoryValidation", "minLengthValidation", "maxLengthValidation", "patternValidation", "dateLimitationValidation", "tooltip", "sendToThankYouPage"]);
    return Object.assign(Object.assign(Object.assign(Object.assign({}, restFieldProps), register(alias, {
        required: mandatoryValidation === null || mandatoryValidation === void 0 ? void 0 : mandatoryValidation.errorMessage,
        minLength: transpileValidationRule(minLengthValidation),
        maxLength: transpileValidationRule(maxLengthValidation),
        pattern: transpileValidationRule({
            value: parseRegex(patternValidation === null || patternValidation === void 0 ? void 0 : patternValidation.value),
            errorMessage: patternValidation === null || patternValidation === void 0 ? void 0 : patternValidation.errorMessage,
        }),
        validate: (value) => validateDateLimitation(value, dateLimitationValidation),
        value: defaultValue,
    })), { errorMessage: (_a = errors[alias]) === null || _a === void 0 ? void 0 : _a.message, tooltipText: tooltip, id: `${uniqueFormPrefix}_${apiProps.id}` }), (apiProps.type === FORM_TYPES.AttachFileField ? { setValue } : null));
};
const redirectFunction = (url, callback) => {
    if (callback) {
        callback(url);
    }
    else {
        window.open(url, '_self');
    }
};
const isFormInactive = (startDateSettings, endDateSettings, callback) => {
    if (startDateSettings) {
        const newDate = utcToZonedTime(new Date(), startDateSettings.timeZoneInfo);
        const utcDate = utcToZonedTime(startDateSettings.localDateTime, startDateSettings.timeZoneInfo);
        if (isBefore(newDate, utcDate)) {
            redirectFunction(startDateSettings.redirectUrl, callback);
        }
    }
    if (endDateSettings) {
        const newDate = utcToZonedTime(new Date(), endDateSettings.timeZoneInfo);
        const utcDate = utcToZonedTime(endDateSettings.localDateTime, endDateSettings.timeZoneInfo);
        if (isAfter(newDate, utcDate)) {
            redirectFunction(endDateSettings.redirectUrl, callback);
        }
    }
};

const KB = 1024;
const DEFAULT_ACCEPTED_FILE_TYPES = {
    infoMessage: 'You may upload {{acceptedFileTypes}} files.',
    placeholderToReplace: '{{acceptedFileTypes}}',
};
const DEFAULT_MAX_FILE_SIZE = {
    infoMessage: 'Max file size: {{value}}MB',
    placeholderToReplace: '{{value}}',
};
const convertToMB = (size) => parseFloat((size / (KB * KB)).toFixed(2));
const convertBase64 = (file) => new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
        resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
        reject(error);
    };
});
const base64Handler = (files) => __awaiter(void 0, void 0, void 0, function* () {
    const filePathsPromises = files.map((file) => convertBase64(file));
    const filePaths = yield Promise.all(filePathsPromises);
    return filePaths.map((base64File, index) => ({
        value: base64File,
        name: files[index].name,
        size: files[index].size,
        type: files[index].type,
    }));
});
const setAcceptedFileTypesLabel = (acceptedFileTypesValidation) => acceptedFileTypesValidation.infoMessage
    ? acceptedFileTypesValidation.infoMessage.replace(acceptedFileTypesValidation.placeholderToReplace, `${acceptedFileTypesValidation.acceptedFileTypes
        .join(', ')
        .toLowerCase()}`)
    : DEFAULT_ACCEPTED_FILE_TYPES.infoMessage.replace(DEFAULT_ACCEPTED_FILE_TYPES.placeholderToReplace, `${acceptedFileTypesValidation.acceptedFileTypes
        .join(', ')
        .toLowerCase()}`);
const setMaxFileSizeValidationLabel = (maxFileSizeValidation) => maxFileSizeValidation.infoMessage
    ? maxFileSizeValidation.infoMessage.replace(maxFileSizeValidation.placeholderToReplace, `${maxFileSizeValidation.value}`)
    : DEFAULT_MAX_FILE_SIZE.infoMessage.replace(DEFAULT_MAX_FILE_SIZE.placeholderToReplace, `${maxFileSizeValidation.value}`);

const AttachFileField = forwardRef(({ name, browseFileLabel = 'Browse your files', acceptedFileTypesValidation, maxFileSizeValidation, id, label, description, tooltipText, tooltipLabel, disabled, maxNumberOfFilesValidation, onChange, onDrop, setValue, }, ref) => {
    const [addedFiles, setAddedFiles] = useState();
    const [errorMessage, setErrorMessage] = useState();
    useEffect(() => {
        setErrorMessage('');
        const setFiles = () => __awaiter(void 0, void 0, void 0, function* () {
            const filesWithBase64 = yield base64Handler(addedFiles);
            setValue(name, filesWithBase64, {
                shouldValidate: true,
                shouldDirty: true,
            });
        });
        if (addedFiles)
            setFiles();
    }, [addedFiles]);
    const checkValidation = (allFiles) => {
        if (!allFiles === null || !(allFiles === null || allFiles === void 0 ? void 0 : allFiles[0]))
            return false;
        if (maxNumberOfFilesValidation &&
            Array.from(allFiles).length > maxNumberOfFilesValidation.value) {
            setErrorMessage(maxNumberOfFilesValidation.errorMessage);
            return false;
        }
        if (maxFileSizeValidation) {
            const isSizeValid = !Array.from(allFiles).some((file) => convertToMB(file.size) > (maxFileSizeValidation === null || maxFileSizeValidation === void 0 ? void 0 : maxFileSizeValidation.value) || 0);
            if (!isSizeValid) {
                setErrorMessage(maxFileSizeValidation.errorMessage);
                return false;
            }
        }
        setAddedFiles((prevFiles) => prevFiles
            ? [...prevFiles, ...Array.from(allFiles)]
            : Array.from(allFiles));
        return true;
    };
    const handleOnChange = useCallback((event) => {
        const { files } = event.target;
        const isValidationPassed = checkValidation(files);
        if (isValidationPassed && onChange) {
            onChange(event);
        }
    }, [onChange]);
    const handleOnDrop = useCallback((event) => {
        if (event.dataTransfer) {
            const { files } = event.dataTransfer;
            const isValidationPassed = checkValidation(files);
            if (isValidationPassed && onDrop) {
                onDrop(event);
            }
        }
    }, [onDrop]);
    const removeFile = (fileName) => {
        setAddedFiles(addedFiles === null || addedFiles === void 0 ? void 0 : addedFiles.filter((file) => file.name !== fileName));
    };
    return (jsxs("div", Object.assign({ className: "cds-promo-form-attach-field" }, { children: [jsx(FieldHead, { id: id, label: label, description: description, tooltipText: tooltipText, tooltipLabel: tooltipLabel }), jsx("input", { ref: ref, className: "cds-promo-form-attach-field__file-input", id: `pp-${name}`, accept: acceptedFileTypesValidation.acceptedFileTypes
                    .map((item) => `.${item.toLowerCase()}`)
                    .join(','), "data-test": "cds-promo-form-attach-field", name: name, multiple: maxNumberOfFilesValidation && maxNumberOfFilesValidation.value > 1, onChange: handleOnChange, type: "file" }), jsxs("label", Object.assign({ htmlFor: `pp-${name}`, onDrop: handleOnDrop, className: classNames('cds-promo-form-attach-field__label', {
                    'cds-promo-form-attach-field__label--disabled': disabled,
                    'cds-promo-form-attach-field__label--error': Boolean(errorMessage),
                }) }, { children: [jsx("span", { className: classNames('cds-promo-form-attach-field__button', {
                            'cds-promo-form-attach-field__button--disabled': disabled,
                        }), role: "button", "aria-label": browseFileLabel || label }), browseFileLabel && (jsx("span", Object.assign({ className: "cds-promo-form-attach-field__info" }, { children: browseFileLabel })))] })), acceptedFileTypesValidation && (jsx("div", Object.assign({ className: "cds-promo-form-attach-field__accepted-file-types" }, { children: jsx("span", { children: setAcceptedFileTypesLabel(acceptedFileTypesValidation) }) }))), maxFileSizeValidation && (jsx("div", Object.assign({ className: "cds-promo-form-attach-field__max-file-size" }, { children: jsx("span", { children: setMaxFileSizeValidationLabel(maxFileSizeValidation) }) }))), jsx(ErrorMessage, { className: "cds-promo-form-attach-field__error", message: errorMessage }), addedFiles && (jsx("div", Object.assign({ className: "cds-promo-form-attach-field__files" }, { children: addedFiles.map((file) => (jsxs("div", Object.assign({ className: "cds-promo-form-attach-field__file" }, { children: [jsx(Icon, { icon: "file" }), jsx("div", Object.assign({ className: "cds-promo-form-attach-field__infoContainer" }, { children: jsx("div", Object.assign({ className: "cds-promo-form-attach-field__name" }, { children: file.name })) })), jsx("button", Object.assign({ type: "button", className: "cds-promo-form-attach-field__removeButton", onClick: () => removeFile(file.name) }, { children: jsx(Icon, { icon: "trashRemove" }) }))] }), file.name))) })))] })));
});

const FormComponentsRenderer = ({ WrappedComponent, watch, unregister, settings, utmParameters, register, errors, setValue, uniqueFormPrefix, props, }) => {
    const shouldRender = useConditionsWatch(watch, props.conditions);
    useEffect(() => {
        if (!shouldRender && props.alias) {
            unregister(props.alias);
        }
    }, [shouldRender, unregister, props.alias]);
    if (!shouldRender)
        return null;
    const preparedProps = prepareFieldProps({
        apiProps: props,
        settings,
        utmParameters,
        register,
        errors,
        setValue,
        uniqueFormPrefix,
    });
    return jsx(WrappedComponent, Object.assign({}, preparedProps));
};

const SeparatorIcon = () => (jsx("svg", Object.assign({ xmlns: "http://www.w3.org/2000/svg", width: "30.828", height: "54.828", viewBox: "0 0 30.828 54.828" }, { children: jsxs("g", Object.assign({ id: "Group_1175", "data-name": "Group 1175", transform: "translate(-191.586 -382.336)" }, { children: [jsxs("g", Object.assign({ id: "Group_1143", "data-name": "Group 1143", transform: "translate(-469.5 -269.75)" }, { children: [jsx("line", { id: "Line_32", "data-name": "Line 32", x2: "14", y2: "14", transform: "translate(662.5 653.5)", fill: "none", stroke: "#707070", strokeLinecap: "round", strokeWidth: "2" }), jsx("line", { id: "Line_33", "data-name": "Line 33", y1: "14", x2: "14", transform: "translate(676.5 653.5)", fill: "none", stroke: "#707070", strokeLinecap: "round", strokeWidth: "2" })] })), jsxs("g", Object.assign({ id: "Group_1144", "data-name": "Group 1144", transform: "translate(-469.5 -264.75)" }, { children: [jsx("line", { id: "Line_35", "data-name": "Line 35", x2: "14", y2: "14", transform: "translate(662.5 667.5)", fill: "none", stroke: "#d4d4d4", strokeLinecap: "round", strokeWidth: "2" }), jsx("line", { id: "Line_34", "data-name": "Line 34", y1: "14", x2: "14", transform: "translate(676.5 667.5)", fill: "none", stroke: "#d4d4d4", strokeLinecap: "round", strokeWidth: "2" })] })), jsxs("g", Object.assign({ id: "Group_1145", "data-name": "Group 1145", transform: "translate(-469.5 -259.75)" }, { children: [jsx("line", { id: "Line_37", "data-name": "Line 37", x2: "14", y2: "14", transform: "translate(662.5 681.5)", fill: "none", stroke: "#f2f2f2", strokeLinecap: "round", strokeWidth: "2" }), jsx("line", { id: "Line_36", "data-name": "Line 36", y1: "14", x2: "14", transform: "translate(676.5 681.5)", fill: "none", stroke: "#f2f2f2", strokeLinecap: "round", strokeWidth: "2" })] }))] })) })));

const PromoFormHeading = ({ data, align, }) => (jsxs(Fragment, { children: [data ? (jsx(HeadingBlock, { align: align, className: "cds-promo-form__heading", headline: data.title, headlineTag: "h2", description: data.description })) : (jsx(Loader, {})), data ? (jsx("div", Object.assign({ className: classNames('cds-promo-form__separator', `cds-justify--${align}`) }, { children: jsx(SeparatorIcon, {}) }))) : null] }));

const SelectWrapper = forwardRef((_a, ref) => {
    var { onChange, name } = _a, rest = __rest(_a, ["onChange", "name"]);
    const patchedOnChange = useCallback((selectValue) => {
        if (onChange) {
            onChange({
                target: {
                    name,
                    value: selectValue,
                },
            });
        }
    }, [name, onChange]);
    return (jsx(Dropdown, Object.assign({}, rest, { name: name, onChange: patchedOnChange, ref: ref })));
});

const SelectListField = forwardRef((_a, ref) => {
    var { maxNumberOfAnswersValidation, name, onChange } = _a, rest = __rest(_a, ["maxNumberOfAnswersValidation", "name", "onChange"]);
    const patchedOnRadioChange = useCallback(({ value }) => {
        if (onChange) {
            onChange({ target: { value, name } });
        }
    }, [name, onChange]);
    if (maxNumberOfAnswersValidation &&
        maxNumberOfAnswersValidation.value > 1) {
        return jsx(SelectWrapper, Object.assign({}, rest, { isMulti: true, name: name, ref: ref }));
    }
    return jsx(RadioList, Object.assign({}, rest, { onChange: patchedOnRadioChange, ref: ref }));
});

const PromoForm = ({ url, settings, formId, submitErrorMessage, onSuccessCallback, onRedirectCallback, utmParameters, align = 'center', showHeading = true, disabled, }) => {
    const queryClient = useQueryClient();
    const { data } = useQuery(`promoData_${formId}`, () => getFormData(url, formId));
    const [token, setToken] = useState('');
    const [formError, setFormError] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const [uniqueFormPrefix] = useState(() => `${Math.random().toString(16).slice(2).substring(0, 8)}ui`);
    const { handleSubmit, register, formState: { isSubmitting, errors }, reset, setValue, watch, unregister, getValues, trigger, } = useForm();
    const componentsMap = Object.assign({ ShortTextField: InputField, LongTextField, BooleanField: CheckboxField, SelectField: SelectWrapper, LegalField,
        SelectListField,
        AttachFileField,
        DateField,
        CheckboxList }, ((settings === null || settings === void 0 ? void 0 : settings.components) || {}));
    useEffect(() => {
        if (!data)
            return;
        isFormInactive(data.startDateSettings, data.endDateSettings, onRedirectCallback);
    }, [data, onRedirectCallback]);
    const onSuccess = (redirectUrl) => {
        if (onSuccessCallback) {
            onSuccessCallback(redirectUrl, reset);
        }
        else {
            window.open(redirectUrl, '_self');
        }
    };
    const handleToken = (value) => {
        if (token)
            return;
        setToken(value);
    };
    const onSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        if (!data)
            return;
        const triggerResult = yield trigger();
        if (!triggerResult) {
            return;
        }
        const body = prepareBody(data, values, queryClient);
        try {
            yield sendFormData({ url, formId, body, token });
            onSuccess(data.thankYouPageUrl);
        }
        catch (_a) {
            setFormError(submitErrorMessage);
        }
    });
    const handleNext = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!data)
            return;
        const values = getValues();
        const fieldsToValidation = data.sections[currentStep].fields.map((props) => props.alias);
        const triggerResult = yield trigger(fieldsToValidation);
        if (!triggerResult) {
            return;
        }
        if (currentStep < data.sections.length - 1) {
            setTimeout(() => {
                setCurrentStep(currentStep + 1);
            }, 200);
        }
        if (data.sendDataOnEachSectionSubmit) {
            const body = prepareBody(data, values, queryClient);
            try {
                yield sendFormData({ url, formId, body, token });
            }
            catch (_b) {
                setFormError(submitErrorMessage);
            }
        }
    });
    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };
    const groupFields = (fields) => {
        const renderedFields = [];
        let checkboxList = {
            type: FORM_TYPES.CheckboxList,
            fields: [],
        };
        fields === null || fields === void 0 ? void 0 : fields.forEach((field) => {
            var _a;
            if (field.type === 'BooleanField' || field.type === 'LegalField') {
                checkboxList.fields.push(field);
                checkboxList.errorMessage =
                    checkboxList.errorMessage || ((_a = errors[field.alias]) === null || _a === void 0 ? void 0 : _a.message);
            }
            else {
                if (checkboxList.fields.length > 0) {
                    renderedFields.push(checkboxList);
                    checkboxList = { type: FORM_TYPES.CheckboxList, fields: [] };
                }
                renderedFields.push(field);
            }
        });
        if (checkboxList.fields.length > 0) {
            renderedFields.push(checkboxList);
        }
        return renderedFields;
    };
    return (jsxs(GoogleReCaptchaProvider, Object.assign({ reCaptchaKey: data === null || data === void 0 ? void 0 : data.recaptchaSiteKey }, { children: [jsxs("form", Object.assign({ onSubmit: handleSubmit(onSubmit), className: "cds-promo-form" }, { children: [showHeading ? jsx(PromoFormHeading, { data: data, align: align }) : null, (data === null || data === void 0 ? void 0 : data.sections) && data.sections[currentStep] ? (jsxs(Fragment, { children: [jsx("fieldset", Object.assign({ className: "cds-promo-form__fields", disabled: disabled }, { children: groupFields(data === null || data === void 0 ? void 0 : data.sections[currentStep].fields).map((fieldGroup) => fieldGroup.type === 'CheckboxList' ? (jsx(CheckboxList, Object.assign({ errorMessage: fieldGroup.errorMessage }, { children: fieldGroup.fields.map((field) => (jsx(FormComponentsRenderer, { WrappedComponent: componentsMap[field.type], props: field, register: register, setValue: setValue, unregister: unregister, watch: watch, errors: errors, uniqueFormPrefix: uniqueFormPrefix, utmParameters: utmParameters, settings: settings }, field.id))) }))) : (jsx(FormComponentsRenderer, { WrappedComponent: componentsMap[fieldGroup.type], props: fieldGroup, register: register, setValue: setValue, unregister: unregister, watch: watch, errors: errors, uniqueFormPrefix: uniqueFormPrefix, utmParameters: utmParameters, settings: settings }, fieldGroup.id))) }), data.sections[currentStep].id), data.sections.length > 1 ? (jsxs("div", Object.assign({ className: "cds-promo-form__section-button-container" }, { children: [data.sections[currentStep].backButtonText ? (jsx(Button, Object.assign({ type: "button", className: "cds-promo-form__section-button", onClick: handlePrevious, "aria-label": data.sections[currentStep].backButtonDescription ||
                                            data.sections[currentStep].backButtonText, disabled: currentStep === 0 }, { children: data.sections[currentStep].backButtonText || '' }))) : null, data.sections[currentStep].submitButtonText ? (jsx(Button, Object.assign({ onClick: currentStep === data.sections.length - 1
                                            ? undefined
                                            : handleNext, type: currentStep === data.sections.length - 1
                                            ? 'submit'
                                            : 'button', className: "cds-promo-form__section-button", "aria-label": data.sections[currentStep].submitButtonDescription ||
                                            data.sections[currentStep].submitButtonText }, { children: data.sections[currentStep].submitButtonText }))) : null] }))) : null] })) : null, jsx(ErrorMessage, { message: formError }), data &&
                        currentStep === data.sections.length - 1 &&
                        data.sections.length === 1 ? (jsx(Button, Object.assign({ type: "submit", size: "small", variant: "primary", className: "cds-promo-form__submit-button", disabled: isSubmitting || disabled, "aria-label": data.submitButtonDescription || data.submitButtonText }, { children: data.submitButtonText }))) :
                        null] })), jsx(GoogleReCaptcha, { action: formId.replace(/-/g, ''), onVerify: handleToken })] })));
};

const queryClient = new QueryClient();
const PromoFormBlock = forwardRef(({ className, url, settings, formId, submitErrorMessage, onSuccessCallback, onRedirectCallback, utmParameters, align, showHeading, disabled, thankYouPageRedirect = false, }, ref) => (jsx("div", Object.assign({ ref: ref, className: classNames('cds-promo-form-block', className) }, { children: jsx(QueryClientProvider, Object.assign({ client: queryClient }, { children: jsx(PromoForm, { url: url, settings: settings, formId: formId, submitErrorMessage: submitErrorMessage, utmParameters: utmParameters, onSuccessCallback: onSuccessCallback, onRedirectCallback: onRedirectCallback, align: align, showHeading: showHeading, disabled: disabled, thankYouPageRedirect: thankYouPageRedirect }) })) }))));

export { PromoFormBlock };
